// Page breaks for the report PDF (an html2canvas snapshot cut into A4 pages).
// Rule: a page never ends through a line of text, an image or a chart.
// It prefers the edge of a card; failing that, the gap between two lines;
// only something taller than a whole page is ever cut.

// Sorted, merged [top, bottom] spans that a cut must not fall inside.
export function mergeSpans(spans) {
  const sorted = spans
    .filter(([top, bottom]) => bottom - top > 0.5)
    .slice()
    .sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [top, bottom] of sorted) {
    const last = merged[merged.length - 1];
    if (last && top < last[1]) last[1] = Math.max(last[1], bottom);
    else merged.push([top, bottom]);
  }
  return merged;
}

// The lowest y <= target that is not inside a span. Spans are disjoint once
// merged, so moving to the top of the span that holds y is always safe.
export function safeCutAtOrAbove(target, spans) {
  for (const [top, bottom] of spans) {
    if (top >= target) break;
    if (target < bottom) return top;
  }
  return target;
}

function isSafe(y, spans) {
  return safeCutAtOrAbove(y, spans) === y;
}

/**
 * Where the page that starts at `start` should end.
 * @param {object} p
 * @param {number} p.start      canvas px where this page starts
 * @param {number} p.pageHeight canvas px one page can hold
 * @param {number} p.total      canvas height
 * @param {number[]} p.edges    sorted card/block edges (preferred cut points)
 * @param {Array<[number, number]>} p.spans merged text/image spans (never cut)
 * @param {number[]} p.forced   sorted points that must end a page
 */
export function choosePageEnd({ start, pageHeight, total, edges, spans, forced = [] }) {
  const ideal = Math.min(start + pageHeight, total);
  const mustEnd = forced.find((point) => point > start + 1 && point <= ideal);
  if (mustEnd != null) return mustEnd;
  if (ideal >= total) return total;

  // A card edge in the lower part of the page keeps whole cards together
  // without leaving half the page blank.
  const edgeFloor = start + pageHeight * 0.45;
  let edge = null;
  for (const point of edges) {
    if (point > ideal) break;
    if (point >= edgeFloor && isSafe(point, spans)) edge = point;
  }
  if (edge != null) return edge;

  // Otherwise the gap between lines closest to the bottom of the page.
  const gap = safeCutAtOrAbove(ideal, spans);
  if (gap > start + pageHeight * 0.1) return gap;

  // Only a block taller than the page is left: it has to be cut.
  return ideal;
}

// Canvas scale that stays inside browser canvas limits: about 16.7 million
// pixels on iPhone Safari and 32,767 px a side elsewhere. Past them the
// canvas comes back blank and the PDF is empty.
export function safeCanvasScale(widthCss, heightCss, wanted = 1.5) {
  if (!widthCss || !heightCss) return wanted;
  const byArea = Math.sqrt(16_000_000 / (widthCss * heightCss));
  const bySide = 32_000 / Math.max(widthCss, heightCss);
  return Math.max(0.5, Math.min(wanted, byArea, bySide));
}
